import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';


const BusParkingGame = () => {
  const [gameState, setGameState] = useState('waiting'); // 'waiting', 'playing', 'success', 'failed'
  const [busPosition, setBusPosition] = useState({ x: 400, y: 350 }); // Start at bottom middle
  const [busAngle, setBusAngle] = useState(-90); // Start facing up (-90 degrees)
  const [busSpeed] = useState(1.5); // Constant slow speed
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);

  // Game area dimensions
  const gameWidth = 800;
  const gameHeight = 400;
  
  // Bus dimensions (horizontal layout)
  const busWidth = 80;
  const busHeight = 25;
  
  // Street dimensions (horizontal, 3 times bus width)
  const streetWidth = busWidth * 1.4; // 240px wide
  const streetHeight = gameHeight;
  const streetX = (gameWidth - streetWidth) / 2; // Center the street
  
  // Bus stop position (target area) - at the right side of street, middle height
  const busStop = {
    x: streetX + streetWidth - busHeight + busHeight, // Right side of street
    y: gameHeight /3 - busHeight, // Center vertically in screen
    width: busHeight + 20,
    height: busWidth + 60 // Slightly taller than bus
  };

  // Check if bus is at bus stop
  const checkBusAtStop = useCallback(() => {
    const busCenter = {
      x: busPosition.x + busWidth / 2,
      y: busPosition.y + busHeight / 2
    };
    
    const stopCenter = {
      x: busStop.x + busStop.width / 2,
      y: busStop.y + busStop.height / 2
    };
    
    const distance = Math.sqrt(
      Math.pow(busCenter.x - stopCenter.x, 2) + 
      Math.pow(busCenter.y - stopCenter.y, 2)
    );
    
    // Check if bus is close enough and properly aligned horizontally
    return distance < 30 && Math.abs(busAngle) < 10;
  }, [busPosition, busAngle]);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const gameLoop = setInterval(() => {
      setBusPosition(prev => {
        const radians = (busAngle * Math.PI) / 180;
        const newX = prev.x + Math.cos(radians) * busSpeed; // Movement based on angle
        const newY = prev.y + Math.sin(radians) * busSpeed; // Movement based on angle
        
        // Keep bus within street boundaries, but allow access to bus stop bay
        const minX = streetX - 30; // Allow 10px onto left grass
        const maxX = streetX + streetWidth - busWidth + busStop.width + 40; // Allow 10px onto right grass
        const minY = 0;
        const maxY = gameHeight - busHeight;
        
        const boundedX = Math.max(minX, Math.min(maxX, newX));
        const boundedY = Math.max(minY, Math.min(maxY, newY));
        
        return { x: boundedX, y: boundedY };
      });
      
      // Check win condition
      if (checkBusAtStop()) {
        setGameState('success');
        setScore(prev => prev + 100 * level);
      }
    }, 50);

    return () => clearInterval(gameLoop);
  }, [gameState, busAngle, busSpeed, checkBusAtStop, level]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameState !== 'playing') return;
      
      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A':
          setBusAngle(prev => prev - 2); // Turn left
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          setBusAngle(prev => prev + 2); // Turn right
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState]);

  const startGame = () => {
    setGameState('playing');
    // Keep current bus position, just change game state
    setBusAngle(-90); // Facing up
  };

  const resetGame = () => {
    setGameState('waiting');
    setBusPosition({ x: gameWidth / 2 - busWidth / 2, y: gameHeight - busHeight - 20 });
    setBusAngle(-90);
  };

  const nextLevel = () => {
    setLevel(prev => prev + 1);
    setGameState('playing');
    setBusPosition({ 
      x: gameWidth / 2 - busWidth / 2 + (Math.random() * 100 - 50), 
      y: gameHeight - busHeight - 20 - Math.random() * 50
    });
    setBusAngle(-90 + (Math.random() * 20 - 10)); // Random small angle from facing up
  };

  return (
    <Paper sx={{ padding: 2, margin: 2, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        🚌 Bus Parking Game
      </Typography>
      
      <Typography variant="h6" sx={{ mb: 2 }}>
        Level: {level} | Score: {score}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2 }}>
        Steuere den Bus mit den Pfeiltasten (← →) oder A/D zur Bushaltestelle!
      </Typography>

      {/* Game Area */}
      <Box
        sx={{
          position: 'relative',
          width: gameWidth,
          height: gameHeight,
          border: '2px solid #333',
          backgroundColor: '#90EE90', // Grass background
          margin: '0 auto',
          overflow: 'hidden'
        }}
      >
        {/* Street (horizontal) */}
        <Box
          sx={{
            position: 'absolute',
            left: streetX,
            top: 0,
            width: streetWidth,
            height: streetHeight,
            backgroundColor: '#666',
            border: '2px solid #333'
          }}
        />

        {/* Bus Stop Bay */}
        <Box
          sx={{
            position: 'absolute',
            left: busStop.x,
            top: busStop.y,
            width: busStop.width,
            height: busStop.height,
            backgroundColor: '#2f2f2fff',
            border: '2px dashed #464646ff',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#fff',
            zIndex: 1
          }}
        >
          BUS STOP
        </Box>

        {/* Bus (horizontal, front facing right) */}
        <Box
          sx={{
            position: 'absolute',
            left: busPosition.x,
            top: busPosition.y,
            width: busWidth,
            height: busHeight,
            backgroundColor: '#00621fff',
            border: '2px solid #003a13ff',
            borderRadius: '8px',
            transform: `rotate(${busAngle}deg)`,
            transformOrigin: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            transition: 'none',
            zIndex: 2
          }}
        >
          ⬜&nbsp;&nbsp;&nbsp;⬜
        </Box>

        {/* Game State Overlays */}
        {gameState === 'waiting' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              color: 'white'
            }}
          >
            <Typography variant="h5" sx={{ mb: 2 }}>
              Bereit zum Parken?
            </Typography>
            <Button variant="contained" onClick={startGame}>
              Spiel Starten
            </Button>
          </Box>
        )}

        {gameState === 'success' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 255, 0, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              🎉 Perfekt geparkt!
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              +{100 * level} Punkte
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={nextLevel}>
                Nächstes Level
              </Button>
              <Button variant="outlined" onClick={resetGame} sx={{ color: 'white', borderColor: 'white' }}>
                Neues Spiel
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Controls Info */}
      <Typography variant="body2" sx={{ mt: 2, color: 'gray' }}>
        Steuerung: Pfeiltasten ↑ ↓ oder A/D | Der Bus fährt automatisch nach rechts
      </Typography>
    </Paper>
  );
};

export { BusParkingGame };
